import { useCallback, useEffect, useRef, useState } from "react";

const HIGHLIGHT_TTL_MS = 5_000;

export interface ConflictHighlightApi {
  conflictedIds: ReadonlySet<string>;
  flagConflict: (ids: string[]) => void;
  clearConflict: () => void;
}

/**
 * Short-lived Set<string> of pre-order IDs to visually flag after a failed
 * batch-merge preflight or server-side rejection. Auto-clears after
 * HIGHLIGHT_TTL_MS so the cue doesn't linger past the next action — the
 * dialog and toast carry the persistent diagnosis; the ring is just the
 * "where" pointer.
 */
export function useConflictHighlight(): ConflictHighlightApi {
  const [ids, setIds] = useState<Set<string>>(() => new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flagConflict = useCallback((flagged: string[]) => {
    setIds(new Set(flagged));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIds(new Set()), HIGHLIGHT_TTL_MS);
  }, []);

  const clearConflict = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIds(new Set());
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { conflictedIds: ids, flagConflict, clearConflict };
}
